import { useCallback, useEffect, useState } from 'react';
import apiClient from '../utils/axiosConfig';

export interface ChatMessage {
  id: number;
  content: string;
  senderType: 'USER' | 'ADMIN' | 'SYSTEM';
  messageType: 'TEXT' | 'IMAGE' | 'FILE' | 'SYSTEM';
  isRead: boolean;
  roomId?: string;
  createdAt: string;
  sender?: {
    id: number;
    name: string;
    email: string;
  };
}

interface UseChatReturn {
  messages: ChatMessage[];
  roomId: string | null;
  loading: boolean;
  error: string | null;
  sendMessage: (content: string) => Promise<void>;
  markAsRead: () => Promise<void>;
  refreshMessages: () => Promise<void>;
  unreadCount: number;
}

export const useChat = (): UseChatReturn => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [roomId, setRoomId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [authTick, setAuthTick] = useState(0);

  // 获取聊天室ID
  const fetchRoomId = useCallback(async () => {
    // 检查是否有token，没有token则不发送请求
    const token = localStorage.getItem('token');
    if (!token) {
      console.log('用户未登录，跳过获取聊天室ID');
      return null;
    }
    
    try {
      const response = await apiClient.get('/chat/room-id');
      if (response.data.success) {
        setRoomId(response.data.roomId);
        return response.data.roomId;
      }
    } catch (err: any) {
      console.error('获取聊天室ID失败:', err);
      if (err.response?.status === 401) {
        console.log('认证失败，清除本地token');
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }
    }
    return null;
  }, []);

  // 获取消息列表
  const fetchMessages = useCallback(async (roomId: string) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await apiClient.get(`/chat/room/${roomId}/messages`);
      
      if (response.data.success) {
        setMessages(response.data.data || []);
      } else {
        setError(response.data.message || '获取消息失败');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || '获取消息失败');
    } finally {
      setLoading(false);
    }
  }, []);

  // 获取未读消息数量
  const fetchUnreadCount = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      return;
    }
    
    try {
      const response = await apiClient.get('/chat/unread-count');
      if (response.data.success) {
        setUnreadCount(response.data.count || 0);
      }
    } catch (err: any) {
      console.error('获取未读消息数量失败:', err);
      if (err.response?.status === 401) {
        console.log('认证失败，清除本地token');
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }
    }
  }, []);

  // 发送消息
  const sendMessage = useCallback(async (content: string) => {
    if (!content.trim()) return;

    try {
      setError(null);
      
      const response = await apiClient.post('/chat/send', {
        content: content.trim()
      });

      if (response.data.success) {
        // 发送成功后刷新消息列表
        if (roomId) {
          await fetchMessages(roomId);
        }
      } else {
        setError(response.data.message || '发送消息失败');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || '发送消息失败');
    }
  }, [roomId, fetchMessages]);

  // 标记消息为已读
  const markAsRead = useCallback(async () => {
    if (!roomId) return;

    try {
      await apiClient.post(`/chat/room/${roomId}/mark-read`);
      setUnreadCount(0);
    } catch (err: any) {
      console.error('标记消息为已读失败:', err);
    }
  }, [roomId]);

  // 刷新消息列表
  const refreshMessages = useCallback(async () => {
    if (roomId) {
      await fetchMessages(roomId);
      await fetchUnreadCount();
    }
  }, [roomId, fetchMessages, fetchUnreadCount]);

  // 初始化
  useEffect(() => {
    const initializeChat = async () => {
      // 未登录不初始化
      const token = localStorage.getItem('token');
      if (!token) {
        return;
      }
      const currentRoomId = await fetchRoomId();
      if (currentRoomId) {
        await fetchMessages(currentRoomId);
        await fetchUnreadCount();
      }
    };

    initializeChat();
  }, [fetchRoomId, fetchMessages, fetchUnreadCount, authTick]);

  // 监听登录状态变化，登出时清理轮询与数据
  useEffect(() => {
    const onAuthChanged = () => {
      const token = localStorage.getItem('token');
      if (!token) {
        setRoomId(null);
        setMessages([]);
        setUnreadCount(0);
      }
      setAuthTick((v) => v + 1);
    };
    window.addEventListener('auth-changed', onAuthChanged as EventListener);
    window.addEventListener('storage', onAuthChanged as EventListener);
    return () => {
      window.removeEventListener('auth-changed', onAuthChanged as EventListener);
      window.removeEventListener('storage', onAuthChanged as EventListener);
    };
  }, []);

  // 定时刷新消息列表和未读消息数量
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!roomId || !token) return;
    
    const interval = setInterval(async () => {
      await fetchMessages(roomId);
      await fetchUnreadCount();
    }, 8000); // 8秒刷新一次，更快地检测新消息
    
    return () => clearInterval(interval);
  }, [roomId, fetchMessages, fetchUnreadCount, authTick]);

  return {
    messages,
    roomId,
    loading,
    error,
    sendMessage,
    markAsRead,
    refreshMessages,
    unreadCount
  };
};
